/**
 * Collection Tabs Pro Module - Vanilla JS Performance Optimized
 * Handles advanced tab functionalities with load more functionality
 * No jQuery dependency - uses native DOM APIs for better performance
 */

(function() {
  'use strict';

  // Performance optimization: Cache and lookup maps
  const tabCache = new Map();
  const productCache = new Map();
  let loadMoreDebounceTimer = null;
  let isInitialized = false;
  let observer = null;

  // Early exit: Check for containers immediately - zero overhead if none exist
  function checkForContainers() {
    return document.querySelectorAll('[js-ctpro]').length > 0;
  }

  // isInitialized guard prevents duplicate listener attachment
  function attachEventListeners() {
    if (isInitialized) return;

    // Event delegation for all interactions
    document.addEventListener('click', handleTabClick);
    document.addEventListener('click', handleLoadMoreClick);
    document.addEventListener('keydown', handleKeyboardNav);

    isInitialized = true;
  }

  // Initialize when section comes into viewport (lazy initialization)
  function initWhenVisible() {
    const containers = document.querySelectorAll('[js-ctpro]');
    if (!containers.length) return;

    // Single IntersectionObserver for all containers (more efficient than multiple)
    observer = new IntersectionObserver((entries) => {
      let hasIntersecting = false;
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          hasIntersecting = true;
        }
      });

      if (hasIntersecting) {
        attachEventListeners();
        init();
        // Disconnect observer once initialized to free resources
        if (observer) {
          observer.disconnect();
          observer = null;
        }
      }
    }, { rootMargin: '100px' });

    // Observe all containers with single observer instance
    containers.forEach(container => observer.observe(container));
  }

  // Early exit: No containers found, skip all initialization
  function tryInitialize() {
    if (!checkForContainers()) return;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initWhenVisible);
    } else {
      initWhenVisible();
    }
  }

  function init() {
    const containers = document.querySelectorAll('[js-ctpro]');
    if (!containers.length) return;

    // Ensure event listeners are attached (idempotent due to guard)
    attachEventListeners();
  }

  // Start initialization check
  tryInitialize();

  function handleTabClick(e) {
    const tab = e.target.closest('.ctpro__tab');
    if (!tab) return;

    const root = tab.closest('[js-ctpro]');
    const tablist = root.querySelector('[js-tablist]');

    activateTab(root, tab);
    ensureTabInView(tab, tablist);
  }

  function handleLoadMoreClick(e) {
    const btn = e.target.closest('[data-load]');
    if (!btn) return;

    const root = btn.closest('[js-ctpro]');

    // Debounce load more requests
    if (loadMoreDebounceTimer) {
      clearTimeout(loadMoreDebounceTimer);
    }

    loadMoreDebounceTimer = setTimeout(() => {
      handleLoadMore(btn, root);
    }, 100);
  }

  function activateTab(root, activeTab) {
    const rootId = root.id || 'default';

    // Check cache first
    const cached = tabCache.get(rootId);
    if (cached?.activeTab === activeTab) return;

    const tabs = root.querySelectorAll('.ctpro__tab');
    const panels = root.querySelectorAll('.ctpro__panel');

    // Cache if not exists
    if (!cached) {
      tabCache.set(rootId, { tabs, panels, activeTab: null });
    }

    // Use requestAnimationFrame for smooth updates
    requestAnimationFrame(() => {
      tabs.forEach(tab => {
        const isActive = tab === activeTab;
        const panelId = tab.getAttribute('aria-controls');

        tab.setAttribute('aria-selected', isActive);
        tab.tabIndex = isActive ? 0 : -1;

        if (panelId) {
          const panel = root.querySelector(`#${panelId}`);
          if (panel) {
            if (isActive) {
              panel.removeAttribute('hidden');
              panel.setAttribute('aria-hidden', 'false');

              // Check if panel needs lazy loading
              const grid = panel.querySelector('.ctpro__grid');
              if (grid && grid.querySelector('[data-lazy-load]')) {
                loadTabProducts(activeTab, panel, grid);
              }
            } else {
              panel.setAttribute('hidden', '');
              panel.setAttribute('aria-hidden', 'true');
            }
          }
        }
      });

      // Update cache
      const cacheData = tabCache.get(rootId);
      cacheData.activeTab = activeTab;
      tabCache.set(rootId, cacheData);

      activeTab.focus();
    });
  }

  function ensureTabInView(tab, tablist) {
    if (!tablist || window.matchMedia('(min-width: 750px)').matches) return;

    requestAnimationFrame(() => {
      const left = tab.offsetLeft;
      const right = left + tab.offsetWidth;
      const viewL = tablist.scrollLeft;
      const viewR = viewL + tablist.clientWidth;

      if (left < viewL) {
        tablist.scrollTo({ left: left - 12, behavior: 'smooth' });
      } else if (right > viewR) {
        tablist.scrollTo({ left: right - tablist.clientWidth + 12, behavior: 'smooth' });
      }
    });
  }

  function loadTabProducts(tab, panel, grid) {
    const handle = tab.dataset.collHandle;
    const placeholder = grid.querySelector('[data-lazy-load]');

    if (!placeholder) return;

    // Show loading state
    placeholder.innerHTML = '<div class="ctpro__loading" style="text-align: center; padding: 2rem; color: #666;">Loading products...</div>';

    // Use fetch API with AbortController for better performance
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    fetch(`/collections/${handle}?view=collection-grid-ajax&page=1`, {
      signal: controller.signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(response => {
        clearTimeout(timeoutId);
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        return response.text();
      })
      .then(html => {
        // Use DocumentFragment for efficient DOM manipulation
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const items = tempDiv.querySelectorAll('.ctpro__item');

        if (items.length) {
          // Use DocumentFragment to batch append operations
          const fragment = document.createDocumentFragment();
          items.forEach(item => fragment.appendChild(item.cloneNode(true)));

          // Clear the placeholder and add products
          grid.innerHTML = '';
          grid.appendChild(fragment);
        }

        // Mark as loaded and remove lazy loading attribute
        grid.removeAttribute('data-lazy-load');

        // Update the grid dataset to match the loaded content
        grid.dataset.page = '1';
        grid.dataset.pageSize = '32';
        grid.dataset.total = tab.dataset.total || '0';
      })
      .catch(error => {
        clearTimeout(timeoutId);
        console.error('Lazy load error:', error);
        placeholder.innerHTML = '<div class="ctpro__loading" style="text-align: center; padding: 2rem; color: #e74c3c;">Failed to load products. Please try again.</div>';
      });
  }

  function handleKeyboardNav(e) {
    const tablist = e.target.closest('[js-tablist]');
    if (!tablist) return;

    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(e.key)) return;

    e.preventDefault();

    const root = tablist.closest('[js-ctpro]');
    const tabs = Array.from(root.querySelectorAll('.ctpro__tab'));
    let current = tabs.find(t => t === document.activeElement) || tabs[0];
    let idx = tabs.indexOf(current);

    switch (e.key) {
      case 'ArrowRight': idx = (idx + 1) % tabs.length; break;
      case 'ArrowLeft': idx = (idx - 1 + tabs.length) % tabs.length; break;
      case 'Home': idx = 0; break;
      case 'End': idx = tabs.length - 1; break;
    }

    const nextTab = tabs[idx];
    activateTab(root, nextTab);
    ensureTabInView(nextTab, tablist);
  }

  function handleLoadMore(btn, root) {
    const panel = btn.closest('.ctpro__panel');
    const grid = panel.querySelector('.ctpro__grid');
    const live = root.querySelector('[data-live]');

    const pageSize = parseInt(grid.dataset.pageSize) || 32;
    const page = parseInt(grid.dataset.page) || 1;
    const total = parseInt(grid.dataset.total) || 0;
    const handle = grid.dataset.collHandle;
    const nextPage = page + 1;

    if (page * pageSize >= total) {
      btn.remove();
      panel.querySelector('.ctpro__actions')?.classList.add('ctpro__actions--solo');
      return;
    }

    if (btn.disabled) return;

    // Check cache first
    const cacheKey = `${handle}-page-${nextPage}`;
    if (productCache.has(cacheKey)) {
      const cachedHtml = productCache.get(cacheKey);
      processLoadedProducts(cachedHtml, grid, btn, panel, live, nextPage, pageSize, total);
      return;
    }

    btn.disabled = true;
    btn.textContent = 'Loading…';

    // Use fetch API with AbortController for better performance
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    fetch(`/collections/${handle}?view=collection-grid-ajax&page=${nextPage}`, {
      signal: controller.signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(response => {
        clearTimeout(timeoutId);
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        return response.text();
      })
      .then(html => {
        // Cache the response
        productCache.set(cacheKey, html);

        // Use requestAnimationFrame to prevent blocking the main thread
        requestAnimationFrame(() => {
          processLoadedProducts(html, grid, btn, panel, live, nextPage, pageSize, total);
        });
      })
      .catch(error => {
        clearTimeout(timeoutId);
        console.error('Load more error:', error);
        btn.disabled = false;
        btn.textContent = 'Load more';
        if (live) {
          live.textContent = 'Sorry, something went wrong loading more products.';
        }
      });
  }

  function processLoadedProducts(html, grid, btn, panel, live, nextPage, pageSize, total) {
    // Use DocumentFragment for efficient DOM manipulation
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const items = tempDiv.querySelectorAll('.ctpro__item');

    if (items.length) {
      // Use DocumentFragment to batch append operations
      const fragment = document.createDocumentFragment();
      items.forEach(item => fragment.appendChild(item.cloneNode(true)));
      grid.appendChild(fragment);
    }

    grid.dataset.page = nextPage;
    btn.disabled = false;
    btn.textContent = 'Load more';

    const shown = Math.min(nextPage * pageSize, total);
    if (live) {
      live.textContent = `${items.length} more products loaded. ${shown} of ${total} shown.`;
    }

    if (shown >= total) {
      btn.remove();
      const actions = panel.querySelector('[data-actions]');
      if (actions && !actions.querySelector('[data-load]')) {
        actions.querySelector('.ctpro__shopall')?.classList.add('ctpro__shopall--solo');
      }
    }
  }

  // Export for debugging and external access
  window.CollectionTabsPro = {
    clearCache: () => {
      tabCache.clear();
      productCache.clear();
    },
    getCacheStats: () => ({
      tabCacheSize: tabCache.size,
      productCacheSize: productCache.size
    })
  };

  // Cleanup on page hide (bfcache-friendly)
  window.addEventListener('pagehide', () => {
    tabCache.clear();
    productCache.clear();
  });
})();
